import React, { useEffect } from 'react';
import { Typography, Box, Grid, Dialog, DialogContent, useMediaQuery, Divider, Button } from '@mui/material';
import CloseModalIcon from '../../../icons/RoundCloseIcon.svg';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { getCloseAllModals, getCreatePaymentRequest, getResetPrState, selectPaymentRequestsState } from '../sagas/paymentrequestslice';
import { CreatePaymentRequestDto, CreateGenericPayerDto } from '@inosio/payments-sdk';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import { useStyles } from '../../../style/Pages/BasicCompanyInfo';
import { getResetPmPropertiesState } from '../../properties/sagas/pmPropertiesSlice';
import NumberFormat from 'react-number-format';
import BackIcon from '../../../icons/back1.svg';
import { minorToMajorUnits } from '../../../shared/util/formaters';
import { selectBillingState } from '../../billingConfiguration/sagas/billingSlice';
import { createGenericPayer, resetQuickPayState, selectQuickPaymentsState } from '../../../resui/quickpayments/sagas/quickpaymentsSlice';
import SuccessModal from './SuccessModal';

interface ReviewModalProps {
  open: boolean;
  isInvisible?: boolean;
  onClose: () => void;
  onGoBack?: () => void;
}

const ReviewModal = (props: ReviewModalProps) => {
  //constants
  const isOnMobileScreen = useMediaQuery('(max-width:600px)');
  const style = {
    height: isOnMobileScreen ? '' : '555px',
  };
  const dispatch = useDispatch();
  const classes = useStyles();

  //local states
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [openSuccessModal, setOpenSuccessModal] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);

  //selectors
  const { PaymentRequestSetupObj, isCreatingPaymentRequest, isPaymentRequestSuccess, paymentRequestsError, CreatePRResponseDto } = useSelector(selectPaymentRequestsState);
  const { availableBankAccounts } = useSelector(selectBillingState);
  const { isHandlingGenericPayer, isHandlingGenericPayerSuccess, genericPayerResponse } = useSelector(selectQuickPaymentsState);

  //handlers
  const resetEverything = () => {
    props.onClose();
    dispatch({
      type: getResetPrState.type,
    });
    dispatch({
      type: getCloseAllModals.type,
    });
    dispatch({
      type: resetQuickPayState.type,
    });
    dispatch({
      type: getResetPmPropertiesState.type,
    });
  };

  const startApiRequests = () => {
    //if its not the generic payer flow, create the payment request right away with the payer
    if (!PaymentRequestSetupObj.IsPayerGeneric) {
      dispatch({
        type: getCreatePaymentRequest.type,
        payload: {
          PayerUuid: PaymentRequestSetupObj.PayerInfo.Uuid,
          PayeeUuid: PaymentRequestSetupObj.PayeeUuid,
          DueDate: PaymentRequestSetupObj.DueDate,
          TotalAmount: PaymentRequestSetupObj.TotalAmount,
          RequestDetails: PaymentRequestSetupObj.RequestDetails,
          DestinationBankAccountUuid: PaymentRequestSetupObj.DepositBankAccountUuid,
          FirstName: PaymentRequestSetupObj.PayerInfo.FirstName,
          LastName: PaymentRequestSetupObj.PayerInfo.LastName,
          Email: PaymentRequestSetupObj.PayerInfo.Email,
          PropertyUuid: PaymentRequestSetupObj.PropertyUuid
        } as CreatePaymentRequestDto,
      });
    }
    //if its generic payer flow, first create a generic payer and then on its success create a payment request
    else {
      dispatch({
        type: createGenericPayer.type,
        payload: {
          FirstName: PaymentRequestSetupObj.PayerInfo.FirstName,
          LastName: PaymentRequestSetupObj.PayerInfo.LastName,
          Email: PaymentRequestSetupObj.PayerInfo.Email,
          PropertyGroupUuid: PaymentRequestSetupObj.PayeeUuid,
        } as CreateGenericPayerDto,
      });
    }
  };

  //useEffects

  useEffect(() => {
    //spinner should show until both api requests are finished or either one fails

    //if either request is in progress, show spinner
    if (isCreatingPaymentRequest || isHandlingGenericPayer) {
      setIsSpinnerModalOpen(true);
    }

    //if neither are going, reset spinner
    if (!isCreatingPaymentRequest && !isHandlingGenericPayer) {
      setSpinnerStatus('start');
    }

    //if second api is done, or both  have stopped,  then close spinner
    if (isPaymentRequestSuccess || (!isCreatingPaymentRequest && !isHandlingGenericPayer)) setIsSpinnerModalOpen(false);

    return;
  }, [isCreatingPaymentRequest, isPaymentRequestSuccess, isHandlingGenericPayer, isHandlingGenericPayerSuccess]);

  useEffect(() => {
    if (isPaymentRequestSuccess) {
      setOpenSuccessModal(true);
    }
  }, [isPaymentRequestSuccess]);

  useEffect(() => {
    if (PaymentRequestSetupObj.IsPayerGeneric && isHandlingGenericPayerSuccess && genericPayerResponse.Uuid) {
      //this is the second half of the request if the payer is generic, to actually make the payment request
      dispatch({
        type: getCreatePaymentRequest.type,
        payload: {
          PayerUuid: genericPayerResponse.Uuid,
          PayeeUuid: PaymentRequestSetupObj.PayeeUuid,
          DueDate: PaymentRequestSetupObj.DueDate,
          TotalAmount: PaymentRequestSetupObj.TotalAmount,
          RequestDetails: PaymentRequestSetupObj.RequestDetails,
          DestinationBankAccountUuid: PaymentRequestSetupObj.DepositBankAccountUuid,
        } as CreatePaymentRequestDto,
      });
    }
  }, [isHandlingGenericPayerSuccess, genericPayerResponse]);

  return (
    <React.Fragment>
      <Dialog
        open={props.open}
        onClose={props.onClose}
        fullScreen={isOnMobileScreen}
        fullWidth={true}
        disableEnforceFocus
        style={{
          background: 'none',
        }}
        PaperProps={{
          style: {
            pointerEvents: 'auto',
            width: '520px',
            boxShadow: 'none',
            borderRadius: '24px',
          },
        }}
        disableEscapeKeyDown={true}
        BackdropProps={{
          invisible: props.isInvisible ?? false,
        }}
      >
        <DialogContent>
          <Box sx={style}>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={2}>
                {' '}
              </Grid>
              <Grid item xs={8}>
                <Typography
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: '18px',
                    lineHeight: '22px',
                    fontFamily: 'Work Sans',
                  }}
                >
                  Review and Submit
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <img src={CloseModalIcon} alt="close" style={{ cursor: 'pointer', marginLeft: isOnMobileScreen ? '0px' : '40px' }} onClick={props.onClose} />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                {paymentRequestsError?.message?.length ? (
                  <div className={classes.globalError}>
                    <Typography
                      style={{
                        fontFamily: "'Open Sans', sans-serif",
                        fontStyle: 'normal',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '18px',
                        color: '#DB3939',
                      }}
                    >
                      Something went wrong. Please try again later or contact support.
                    </Typography>
                  </div>
                ) : (
                  ''
                )}
              </Grid>
              <Grid item xs={12} display="flex" justifyContent={'space-between'}>
                Payer Details:
              </Grid>
              <Grid item xs={12} display="flex" justifyContent={'space-between'}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '22px',
                    fontFamily: 'Work Sans',
                  }}
                >
                  {`${PaymentRequestSetupObj.PayerInfo.FirstName} ${PaymentRequestSetupObj.PayerInfo.LastName}`}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '22px',
                    fontFamily: 'Work Sans',
                  }}
                >
                  {PaymentRequestSetupObj.PayerInfo.Email}
                </Typography>
              </Grid>
              <Grid item xs={12} display="flex" justifyContent={'space-between'}></Grid>
              <Grid item xs={12} display="flex" justifyContent={'space-between'}>
                Request Details:
              </Grid>
              <Grid item xs={12} display="flex" justifyContent={'space-between'}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '22px',
                    fontFamily: 'Work Sans',
                  }}
                >
                  Deposit Account: ****{availableBankAccounts.find((ba) => ba.bankAccountUuid === PaymentRequestSetupObj.DepositBankAccountUuid)?.last4}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '22px',
                    fontFamily: 'Work Sans',
                  }}
                >
                  {`Due: ${moment(PaymentRequestSetupObj.DueDate).format('MM/DD/YYYY')}`}
                </Typography>
              </Grid>
              <Grid item xs={12} display="flex" justifyContent={'space-between'}>
                &nbsp;
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  maxHeight: paymentRequestsError?.message?.length ? '210px' : '290px',
                  overflow: 'auto',
                  '&::-webkit-scrollbar': {
                    width: 7,
                  },
                  '&::-webkit-scrollbar-track': {
                    boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                    borderRadius: '24px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'darkgrey',
                    borderRadius: '24px',
                  },
                }}
              >
                {PaymentRequestSetupObj.RequestDetails?.map((req, index) => {
                  return (
                    <Grid item xs={12} key={index}>
                      <Grid item xs={12} display="flex" justifyContent={'space-between'} ml={1} mr={2}>
                        <Grid item xs={9.5}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '16px',
                              lineHeight: '22px',
                              fontFamily: 'Work Sans',
                              //   color: '#D3D3D3',
                            }}
                          >
                            {req.Description}
                          </Typography>
                        </Grid>
                        <Grid item xs={2.5}>
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontSize: '16px',
                              lineHeight: '22px',
                              fontFamily: 'Work Sans',
                              //   color: '#D3D3D3',
                              textAlign: 'start',
                            }}
                          >
                            <NumberFormat
                              thousandsGroupStyle="thousand"
                              value={minorToMajorUnits(req.Amount)}
                              prefix="$"
                              decimalSeparator="."
                              displayType="text"
                              type="text"
                              thousandSeparator={true}
                              allowNegative={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                            />
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} mt={1} mb={1} mr={1}>
                        {<Divider sx={{ width: '100%' }} />}
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
              <Grid item sx={{ position: 'absolute', bottom: 50, width: '100%' }}>
                <Divider />
                <Grid container justifyContent={'space-between'}>
                  <Grid item p={2}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '18px',
                        lineHeight: '22px',
                        fontFamily: 'Work Sans',
                      }}
                    >
                      Total
                    </Typography>
                  </Grid>
                  <Grid item p={2}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '18px',
                        lineHeight: '22px',
                        fontFamily: 'Work Sans',
                      }}
                    >
                      <NumberFormat
                        thousandsGroupStyle="thousand"
                        value={minorToMajorUnits(PaymentRequestSetupObj.TotalAmount || 0)}
                        prefix="$"
                        decimalSeparator="."
                        displayType="text"
                        type="text"
                        thousandSeparator={true}
                        allowNegative={true}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item sx={{ position: 'absolute', bottom: 10, right: 17 }}>
              <Button
                variant="contained"
                style={{
                  width: '139px',
                  height: '43px',
                  borderRadius: '24px',
                  textTransform: 'capitalize',
                  color: 'white',
                  backgroundColor: '#39A2DB',
                  borderColor: '#39A2DB',
                }}
                onClick={() => {
                  startApiRequests();
                }}
                id="continueReviewBtn"
              >
                Submit
              </Button>
            </Grid>
          </Box>
          <Grid item sx={{ position: 'absolute', top: 20, left: 15 }}>
            <img
              src={BackIcon}
              alt="back"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                props.onGoBack && props.onGoBack();
              }}
            />
          </Grid>
        </DialogContent>
      </Dialog>
      {isSpinnerModalOpen ? (
        <SpinnerModal
          openSpinnerModal={isSpinnerModalOpen}
          spinnerStatus={spinnerStatus}
          closeCancel={resetEverything}
          handleCloseSpinnerModal={() => {
            setIsSpinnerModalOpen(false);
          }}
          isInvisible={true}
          width="520px"
          height="580px"
        />
      ) : (
        <></>
      )}
      <SuccessModal
        open={openSuccessModal}
        onClose={() => {
          setOpenSuccessModal(false);
          props.onClose();
        }}
        link={CreatePRResponseDto.link}
      />
    </React.Fragment>
  );
};

export default ReviewModal;
